import React from 'react';
import { useProducts } from '../hooks/useProducts';
import { useTemplates } from '../hooks/useTemplates';
import { useMessages } from '../hooks/useMessages';
import { useTasks } from '../hooks/useTasks';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const Dashboard: React.FC = () => {
  const { products } = useProducts();
  const { templates } = useTemplates();
  const { messages } = useMessages();
  const { tasks, getActiveCount } = useTasks();

  const formatTimeAgo = (date: string) => {
    return formatDistanceToNow(new Date(date), { addSuffix: true, locale: ptBR });
  };

  const recentActivities = [
    ...products.slice(0, 3).map(p => ({
      type: 'product' as const,
      icon: 'Building',
      text: `Produto "${p.title}" cadastrado`,
      time: p.created_at,
    })),
    ...messages.filter(m => m.status === 'sent').slice(0, 3).map(m => ({
      type: 'message' as const,
      icon: 'Send',
      text: `Mensagem enviada para ${m.recipient_name}`,
      time: m.created_at,
    })),
    ...tasks.slice(0, 3).map(t => ({
      type: 'task' as const,
      icon: 'Checklist',
      text: `Tarefa "${t.title}" criada`,
      time: t.created_at,
    })),
  ].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
   .slice(0, 5);

  return (
    <div className="space-y-6">
      <div className="stats-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-card">
          <div className="stat-icon products">
            <span className="text-2xl">🏢</span>
          </div>
          <div className="stat-info">
            <h3 className="text-2xl font-medium">{products.length}</h3>
            <p className="text-text-secondary text-sm">Produtos</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon templates">
            <span className="text-2xl">📧</span>
          </div>
          <div className="stat-info">
            <h3 className="text-2xl font-medium">{templates.length}</h3>
            <p className="text-text-secondary text-sm">Templates</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon messages">
            <span className="text-2xl">📤</span>
          </div>
          <div className="stat-info">
            <h3 className="text-2xl font-medium">{messages.filter(m => m.status === 'pending').length}</h3>
            <p className="text-text-secondary text-sm">Mensagens Pendentes</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon tasks">
            <span className="text-2xl">✅</span>
          </div>
          <div className="stat-info">
            <h3 className="text-2xl font-medium">{getActiveCount()}</h3>
            <p className="text-text-secondary text-sm">Tarefas Ativas</p>
          </div>
        </div>
      </div>

      <div className="recent-activity card">
        <h2 className="text-lg font-medium mb-4">Atividade Recente</h2>
        {recentActivities.length === 0 ? (
          <p className="text-text-secondary text-center py-8">Nenhuma atividade recente</p>
        ) : (
          <div className="space-y-3">
            {recentActivities.map((activity, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-surface-1 rounded-sm">
                <span className="text-xl">{activity.icon === 'Building' ? '🏢' : activity.icon === 'Send' ? '📤' : '✅'}</span>
                <span className="flex-1 text-sm">{activity.text}</span>
                <small className="text-text-muted text-xs">{formatTimeAgo(activity.time)}</small>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
